<template>
  <div class="p-4 min-h-screen bg-gray-100">
   <div class="max-w-6xl mx-auto">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-3">
      <h2 class="text-lg font-semibold">Client Assignments</h2>
      <div class="text-sm text-gray-600">{{ filteredClients.length }} of {{ clients.length }} clients</div>
    </div>

    <div class="flex flex-row flex-wrap gap-4 mb-4 items-start text-sm">
      <!-- Account Manager -->
      <div class="flex flex-col gap-1">
        <span class="text-gray-600">Account Manager</span>
        <div class="relative w-56" ref="managerMenu">
          <div
            class="w-full h-[34px] flex items-center gap-1 px-1.5 border rounded-md bg-white cursor-pointer"
            :class="filterManager !== null ? 'border-accent-primary' : 'border-gray-400'"
            @click="toggleMenu('manager')"
          >
            <div class="flex-1 min-w-0 flex items-center">
              <span v-if="filterManager === null" class="text-gray-500">All</span>
              <span v-else class="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 pl-2 pr-1 py-0.5 text-xs max-w-full">
                <span class="truncate">{{ managerFilterLabel }}</span>
                <button
                  type="button"
                  class="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full leading-none hover:bg-blue-200"
                  title="Clear account manager filter"
                  @click.stop="filterManager = null"
                >&times;</button>
              </span>
            </div>
            <span class="shrink-0 text-gray-500">&#9662;</span>
          </div>

          <div v-if="openMenu === 'manager'" class="absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto bg-white border border-gray-400 rounded-md shadow-lg py-1">
            <button type="button" :class="[optionClass, { 'bg-indigo-50 font-medium': filterManager === null }]" @click="selectManager(null)">
              All
            </button>
            <button v-for="user in accountManagers" :key="user.id" type="button" :class="[optionClass, 'truncate', { 'bg-indigo-50 font-medium': filterManager === user.id }]" @click="selectManager(user.id)">
              {{ fullName(user) }}
            </button>
            <button type="button" :class="[optionClass, 'italic text-gray-600', { 'bg-indigo-50 font-medium': filterManager === 'unassigned' }]" @click="selectManager('unassigned')">
              Unassigned
            </button>
          </div>
        </div>
      </div>

      <!-- Account Staff (multi) -->
      <div class="flex flex-col gap-1">
        <span class="text-gray-600">Account Staff</span>
        <div class="relative w-72" ref="staffMenu">
          <div
            class="w-full h-[34px] flex items-center gap-1 px-1.5 border rounded-md bg-white cursor-pointer"
            :class="filterStaff.length > 0 ? 'border-accent-primary' : 'border-gray-400'"
            @click="toggleMenu('staff')"
          >
            <!-- Fixed height with horizontal scroll: the box never grows as
                 chips are added, and the caret stays put outside the scroller. -->
            <div class="flex-1 min-w-0 flex flex-nowrap items-center gap-1 overflow-x-auto">
              <span v-if="filterStaff.length === 0" class="text-gray-500">All</span>
              <span
                v-for="user in selectedStaff"
                :key="user.id"
                class="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 pl-2 pr-1 py-0.5 text-xs whitespace-nowrap"
              >
                {{ fullName(user) }}
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-4 h-4 rounded-full leading-none hover:bg-blue-200"
                  :title="`Remove ${fullName(user)}`"
                  @click.stop="toggleStaffFilter(user.id)"
                >&times;</button>
              </span>
            </div>
            <span class="shrink-0 text-gray-500">&#9662;</span>
          </div>

          <div v-if="openMenu === 'staff'" class="absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto bg-white border border-gray-400 rounded-md shadow-lg py-1">
            <button type="button" :class="[optionClass, { 'bg-indigo-50 font-medium': filterStaff.length === 0 }]" @click="filterStaff = []">
              All
            </button>
            <button v-for="user in accountStaff" :key="user.id" type="button" :class="[optionClass, 'truncate', { 'bg-indigo-50 font-medium': filterStaff.includes(user.id) }]" @click="toggleStaffFilter(user.id)">
              {{ fullName(user) }}
            </button>
          </div>
        </div>
      </div>

      <!-- Client -->
      <div class="flex flex-col gap-1">
        <span class="text-gray-600">Client</span>
        <div class="relative w-64" ref="clientMenu">
          <div
            class="w-full h-[34px] flex items-center gap-1 px-1.5 border rounded-md bg-white cursor-pointer"
            :class="filterClient !== null ? 'border-accent-primary' : 'border-gray-400'"
            @click="toggleMenu('client')"
          >
            <div class="flex-1 min-w-0 flex items-center">
              <span v-if="filterClient === null" class="text-gray-500">All</span>
              <span v-else class="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 pl-2 pr-1 py-0.5 text-xs max-w-full">
                <span class="truncate">{{ clientFilterLabel }}</span>
                <button
                  type="button"
                  class="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full leading-none hover:bg-blue-200"
                  title="Clear client filter"
                  @click.stop="filterClient = null"
                >&times;</button>
              </span>
            </div>
            <span class="shrink-0 text-gray-500">&#9662;</span>
          </div>

          <div v-if="openMenu === 'client'" class="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-400 rounded-md shadow-lg">
            <div class="p-1 border-b border-gray-200">
              <input
                ref="clientSearchInput"
                v-model="clientSearch"
                type="text"
                placeholder="Search clients"
                class="w-full px-2 py-1 border border-gray-300 rounded"
              />
            </div>
            <div class="max-h-56 overflow-y-auto py-1">
              <button type="button" :class="[optionClass, { 'bg-indigo-50 font-medium': filterClient === null }]" @click="selectClient(null)">
                All
              </button>
              <button v-for="client in searchedClients" :key="client.id" type="button" :class="[optionClass, 'truncate', { 'bg-indigo-50 font-medium': filterClient === client.id }]" @click="selectClient(client.id)">
                {{ client.client_name }}
              </button>
              <div v-if="searchedClients.length === 0" class="px-2 py-1 text-gray-500">No matches</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Client Status (multi) -->
      <div class="flex flex-col gap-1">
        <span class="text-gray-600">Client Status</span>
        <div class="relative w-52" ref="statusMenu">
          <div
            class="w-full h-[34px] flex items-center gap-1 px-1.5 border rounded-md bg-white cursor-pointer"
            :class="filterStatus.length > 0 ? 'border-accent-primary' : 'border-gray-400'"
            @click="toggleMenu('status')"
          >
            <div class="flex-1 min-w-0 flex flex-nowrap items-center gap-1 overflow-x-auto">
              <span v-if="filterStatus.length === 0" class="text-gray-500">All</span>
              <span
                v-for="option in selectedStatuses"
                :key="option.value"
                class="shrink-0 inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 pl-2 pr-1 py-0.5 text-xs whitespace-nowrap"
              >
                {{ option.label }}
                <button
                  type="button"
                  class="inline-flex items-center justify-center w-4 h-4 rounded-full leading-none hover:bg-blue-200"
                  :title="`Remove ${option.label}`"
                  @click.stop="toggleStatusFilter(option.value)"
                >&times;</button>
              </span>
            </div>
            <span class="shrink-0 text-gray-500">&#9662;</span>
          </div>

          <div v-if="openMenu === 'status'" class="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-gray-400 rounded-md shadow-lg py-1">
            <button type="button" :class="[optionClass, { 'bg-indigo-50 font-medium': filterStatus.length === 0 }]" @click="filterStatus = []">
              All
            </button>
            <button v-for="option in STATUS_OPTIONS" :key="option.value" type="button" :class="[optionClass, { 'bg-indigo-50 font-medium': filterStatus.includes(option.value) }]" @click="toggleStatusFilter(option.value)">
              {{ option.label }}
            </button>
          </div>
        </div>
      </div>

      <!-- Carries the filters' label/control structure so it shares their
           baseline without a hand-tuned margin. -->
      <div v-if="hasCustomFilter" class="flex flex-col gap-1">
        <span class="text-gray-600 invisible" aria-hidden="true">&nbsp;</span>
        <button
          class="h-[34px] px-3 border border-gray-400 rounded-md hover:bg-gray-100"
          @click="clearFilters"
        >
          Reset
        </button>
      </div>
    </div>

    <div v-if="loading" class="p-6 text-center text-gray-500">Loading...</div>

    <template v-else>
      <!-- ACCOUNT MANAGERS -->
      <section class="mb-8">
        <h3 class="mb-3 px-4">
          <button
            class="flex items-center gap-2 text-lg font-semibold uppercase tracking-wide text-dark-primary"
            @click="toggleAll(managerGroups, !allManagersExpanded)"
          >
            <span class="w-4 text-left">{{ allManagersExpanded ? '▾' : '▸' }}</span>
            Account Managers
          </button>
        </h3>
        <div v-if="managerGroups.length === 0" class="text-sm text-gray-500 px-2 py-3">
          No account managers match these filters.
        </div>
        <div v-for="group in managerGroups" :key="group.key" class="border border-gray-300 rounded-md mb-2 overflow-hidden">
          <button
            class="w-full flex flex-row items-center gap-3 px-4 py-3 text-left text-base font-semibold bg-dark-primary text-white hover:bg-dark-secondary"
            @click="toggle(group.key)"
          >
            <span class="w-4 text-light-primary">{{ expanded[group.key] ? '▾' : '▸' }}</span>
            <span :class="group.user ? '' : 'text-light-primary italic'">
              {{ group.user ? fullName(group.user) : 'Unassigned' }}
            </span>
            <span class="font-normal text-light-primary">({{ group.clients.length }})</span>
          </button>

          <div v-if="expanded[group.key]" class="border-t border-gray-200">
            <div v-if="group.clients.length === 0" class="px-4 py-2 text-sm text-gray-500">No clients.</div>
            <table v-else class="table-fixed text-sm w-full">
              <thead class="bg-gray-200 text-gray-600">
                <tr>
                  <th class="text-left font-semibold pl-11 pr-4 py-2 w-[30%]">Client Name</th>
                  <th class="text-left font-semibold px-4 py-2 w-[20%]">Status</th>
                  <th class="text-left font-semibold px-4 py-2 w-[40%]">Staff</th>
                  <th class="text-right font-semibold pl-4 pr-11 py-2 w-[10%]">History</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="client in group.clients" :key="client.id">
                  <tr class="border-t border-gray-300">
                    <td class="pl-11 pr-4 py-2">{{ client.client_name }}</td>
                    <td class="px-4 py-2">
                      <span :class="statusClass(client.status)">{{ client.status || '—' }}</span>
                    </td>
                    <td class="px-4 py-2 text-xs text-gray-600">{{ staffNames(client) }}</td>
                    <td class="pl-4 pr-11 py-2 text-right">
                      <button
                        class="text-xs text-gray-600 hover:text-dark-primary"
                        @click="toggle(`hist:${client.id}`)"
                      >
                        {{ expanded[`hist:${client.id}`] ? '▾ history' : '▸ history' }}
                      </button>
                    </td>
                  </tr>

                  <tr v-if="expanded[`hist:${client.id}`]" class="bg-gray-50">
                    <td colspan="4" class="px-11 py-3">
                      <div v-if="historyOf(client).length === 0" class="text-xs text-gray-500 italic">
                        No assignment history recorded.
                      </div>
                      <div v-else class="w-fit mx-auto border border-gray-300 rounded-md px-6 py-3">
                        <table class="table-auto text-xs mx-auto">
                          <thead class="text-gray-600">
                            <tr>
                              <th class="text-left font-normal py-1 pl-6 pr-48">Manager</th>
                              <th class="text-left font-normal py-1 pr-48">Assigned On</th>
                              <th class="text-left font-normal py-1 pr-48">Assigned By</th>
                              <th class="text-left font-normal py-1 pr-6">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="row in historyOf(client)" :key="row.id" class="border-t border-gray-200">
                              <td class="py-1 pl-6 pr-48">{{ row.user ? fullName(row.user) : 'Unassigned' }}</td>
                              <td class="py-1 pr-48">{{ formatDate(row.created_on) }}</td>
                              <td class="py-1 pr-48">{{ row.created_by ? fullName(row.created_by) : '—' }}</td>
                              <td class="py-1 pr-6">{{ row.is_active ? 'Active' : 'Ended' }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- ACCOUNT STAFF -->
      <section>
        <h3 class="mb-3 px-4">
          <button
            class="flex items-center gap-2 text-lg font-semibold uppercase tracking-wide text-dark-primary"
            @click="toggleAll(staffGroups, !allStaffExpanded)"
          >
            <span class="w-4 text-left">{{ allStaffExpanded ? '▾' : '▸' }}</span>
            Account Staff
          </button>
        </h3>
        <div v-if="staffGroups.length === 0" class="text-sm text-gray-500 px-2 py-3">
          No account staff match these filters.
        </div>
        <div v-for="group in staffGroups" :key="group.key" class="border border-gray-300 rounded-md mb-2 overflow-hidden">
          <button
            class="w-full flex flex-row items-center gap-3 px-4 py-3 text-left text-base font-semibold bg-dark-primary text-white hover:bg-dark-secondary"
            @click="toggle(group.key)"
          >
            <span class="w-4 text-light-primary">{{ expanded[group.key] ? '▾' : '▸' }}</span>
            <span>{{ fullName(group.user) }}</span>
            <span class="font-normal text-light-primary">({{ group.clients.length }})</span>
          </button>

          <!-- No empty state: staffGroups drops anyone with zero clients. -->
          <div v-if="expanded[group.key]" class="border-t border-gray-200">
            <table class="table-fixed text-sm w-full">
              <thead class="bg-gray-200 text-gray-600">
                <tr>
                  <th class="text-left font-semibold pl-11 pr-4 py-2 w-[30%]">Client Name</th>
                  <th class="text-left font-semibold px-4 py-2 w-[20%]">Status</th>
                  <th class="text-left font-semibold px-4 py-2 w-[20%]">Account Manager</th>
                  <th class="text-left font-semibold px-4 py-2 w-[30%]">Staff</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="client in group.clients" :key="client.id" class="border-t border-gray-300">
                  <td class="pl-11 pr-4 py-2">{{ client.client_name }}</td>
                  <td class="px-4 py-2">
                    <span :class="statusClass(client.status)">{{ client.status || '—' }}</span>
                  </td>
                  <td class="px-4 py-2">
                    <span v-if="client.active_account_manager">
                      {{ fullName(client.active_account_manager.user) }}
                    </span>
                    <span v-else class="text-gray-500 italic">Unassigned</span>
                  </td>
                  <td class="px-4 py-2 text-xs text-gray-600">{{ staffNames(client) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </template>
   </div>
  </div>
</template>

<script>
export default { name: "client-assignments-page" };
</script>

<script setup>
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getClientAssignments } from "@/services/ClientAssignment.js";

const store = useStore();
const $toast = useToast();

const loading = ref(true);
const clients = ref([]);

// Status is a free-text CharField with no choices; these are the live values.
const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "prospect", label: "Prospect" },
  { value: "finders fee", label: "Finders Fee" },
  { value: "tail", label: "Tail" },
  { value: "inactive", label: "Inactive" },
];
const DEFAULT_STATUS = "active";

const filterManager = ref(null);
const filterStaff = ref([]);
const filterClient = ref(null);
const filterStatus = ref([DEFAULT_STATUS]);

// Which dropdown is open: 'status' | 'manager' | 'staff' | 'client' | null. One
// shared ref rather than four booleans, so opening one closes the others for free.
const openMenu = ref(null);

const expanded = reactive({});

// Shared by all four dropdowns; the selected row adds bg-indigo-50 font-medium,
// matching TypeAheadSingleSelectBox.
const optionClass = "w-full block px-2 py-1 text-left hover:bg-indigo-100";

function fullName(user) {
  return `${user.first_name} ${user.last_name}`.trim();
}

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
}

// The active manager's user id, or null when the client is unassigned.
function managerIdOf(client) {
  return client.active_account_manager?.user?.id ?? null;
}

function historyOf(client) {
  return client.account_manager_history ?? [];
}

// Section headers and the person filters are derived from the client list
// rather than a separate roster endpoint, so anyone with no clients is absent.
function peopleFromClients(pick) {
  const byId = new Map();
  clients.value.forEach((client) => pick(client).forEach((user) => byId.set(user.id, user)));
  return [...byId.values()].sort((a, b) => fullName(a).localeCompare(fullName(b)));
}

const accountManagers = computed(() =>
  peopleFromClients((client) =>
    client.active_account_manager ? [client.active_account_manager.user] : []
  )
);

const accountStaff = computed(() => peopleFromClients((client) => client.account_staff_members));

function staffNames(client) {
  const members = client.account_staff_members ?? [];
  return members.length > 0 ? members.map(fullName).join(", ") : "—";
}

function statusClass(status) {
  const base = "px-1.5 py-0.5 rounded text-xs capitalize ";
  if (status === "active") return base + "bg-green-100 text-green-700";
  if (status === "prospect") return base + "bg-blue-100 text-blue-700";
  return base + "bg-gray-200 text-gray-700";
}

function toggle(key) {
  expanded[key] = !expanded[key];
}

const hasPersonFilter = computed(
  () =>
    filterManager.value !== null || filterStaff.value.length > 0 || filterClient.value !== null
);

const isDefaultStatus = computed(
  () => filterStatus.value.length === 1 && filterStatus.value[0] === DEFAULT_STATUS
);

// Any restriction at all, default status included. Drives the hide-empty-groups
// rule, so a manager whose clients are all inactive is not an empty dropdown.
const hasFilter = computed(() => hasPersonFilter.value || filterStatus.value.length > 0);

// Anything the user actually changed, so "Reset" stays hidden until there
// is something to clear.
const hasCustomFilter = computed(() => hasPersonFilter.value || !isDefaultStatus.value);

function clearFilters() {
  filterManager.value = null;
  filterStaff.value = [];
  filterClient.value = null;
  filterStatus.value = [DEFAULT_STATUS];
}

// Built from accountStaff rather than filterStaff so the chips stay in the
// menu's alphabetical order instead of the order they were clicked.
const selectedStaff = computed(() =>
  accountStaff.value.filter((user) => filterStaff.value.includes(user.id))
);

function toggleStaffFilter(userId) {
  filterStaff.value = filterStaff.value.includes(userId)
    ? filterStaff.value.filter((id) => id !== userId)
    : [...filterStaff.value, userId];
}

// Chips follow STATUS_OPTIONS order rather than click order.
const selectedStatuses = computed(() =>
  STATUS_OPTIONS.filter((option) => filterStatus.value.includes(option.value))
);

function toggleStatusFilter(value) {
  filterStatus.value = filterStatus.value.includes(value)
    ? filterStatus.value.filter((status) => status !== value)
    : [...filterStatus.value, value];
}

const managerFilterLabel = computed(() => {
  if (filterManager.value === "unassigned") return "Unassigned";
  const manager = accountManagers.value.find((user) => user.id === filterManager.value);
  return manager ? fullName(manager) : "—";
});

const clientFilterLabel = computed(() => {
  const client = clients.value.find((c) => c.id === filterClient.value);
  return client ? client.client_name : "—";
});

// Single-select menus close on pick; the multi-selects (staff, status) stay
// open so several can be ticked in one go.
function selectManager(value) {
  filterManager.value = value;
  openMenu.value = null;
}

function selectClient(value) {
  filterClient.value = value;
  openMenu.value = null;
}

const clientSearch = ref("");
const clientSearchInput = ref(null);

// Focus on open and reset on close, so reopening never starts mid-search.
watch(openMenu, async (name) => {
  if (name !== "client") {
    clientSearch.value = "";
    return;
  }
  await nextTick();
  clientSearchInput.value?.focus();
});

function toggleMenu(name) {
  openMenu.value = openMenu.value === name ? null : name;
}

// DOM containment rather than the v-click-outside directive: that one tests hit
// coordinates, and these menus are absolutely positioned outside their wrapper's
// box, so picking an option read as "outside" and closed the menu. contains()
// counts the menu as inside wherever it renders.
const statusMenu = ref(null);
const managerMenu = ref(null);
const staffMenu = ref(null);
const clientMenu = ref(null);

function closeMenuOnOutsideClick(event) {
  const box = { status: statusMenu, manager: managerMenu, staff: staffMenu, client: clientMenu }[
    openMenu.value
  ];
  if (box?.value && !box.value.contains(event.target)) {
    openMenu.value = null;
  }
}

// An empty status selection means every status, not none. Its own step so the
// Client dropdown offers only clients status allows; offering an excluded one
// would filter the page down to nothing.
const statusFilteredClients = computed(() =>
  filterStatus.value.length === 0
    ? clients.value
    : clients.value.filter((client) => filterStatus.value.includes(client.status))
);

// What the Client dropdown lists: status-allowed clients, narrowed by its search box.
const searchedClients = computed(() => {
  const term = clientSearch.value.trim().toLowerCase();
  if (!term) return statusFilteredClients.value;
  return statusFilteredClients.value.filter((client) =>
    client.client_name.toLowerCase().includes(term)
  );
});

// Filters are AND-combined. Both sections group out of this one surviving set,
// which is what makes a manager filter also narrow the staff rosters.
const filteredClients = computed(() =>
  statusFilteredClients.value.filter((client) => {
    if (filterManager.value === "unassigned") {
      if (managerIdOf(client) !== null) return false;
    } else if (filterManager.value !== null && managerIdOf(client) !== filterManager.value) {
      return false;
    }
    // AND: the client must carry every selected staff member, not just one.
    if (
      filterStaff.value.length > 0 &&
      !filterStaff.value.every((id) => client.account_staff_members.some((s) => s.id === id))
    ) {
      return false;
    }
    if (filterClient.value !== null && client.id !== filterClient.value) return false;
    return true;
  })
);

// Unfiltered — which needs status cleared to "All" too — every eligible person
// is listed even at zero clients, so an empty roster is visible. Once anything
// is filtered, zero-client rows are noise.
function applyEmptyRule(groups) {
  return hasFilter.value ? groups.filter((group) => group.clients.length > 0) : groups;
}

const managerGroups = computed(() => {
  const groups = accountManagers.value.map((user) => ({
    key: `mgr:${user.id}`,
    user,
    clients: filteredClients.value.filter((client) => managerIdOf(client) === user.id),
  }));
  const unassigned = filteredClients.value.filter((client) => managerIdOf(client) === null);
  if (unassigned.length > 0) {
    groups.push({ key: "mgr:unassigned", user: null, clients: unassigned });
  }
  return applyEmptyRule(groups);
});

// Staff with no clients are always hidden, filtered or not — an eligible person
// who staffs nothing is noise here, unlike an account manager with an empty book.
const staffGroups = computed(() => {
  // With a staff filter on, list only the people picked. Otherwise the section
  // also lists everyone sharing their clients, burying the person being sought.
  const visible =
    filterStaff.value.length > 0
      ? accountStaff.value.filter((user) => filterStaff.value.includes(user.id))
      : accountStaff.value;

  return visible
    .map((user) => ({
      key: `staff:${user.id}`,
      user,
      clients: filteredClients.value.filter((client) =>
        client.account_staff_members.some((member) => member.id === user.id)
      ),
    }))
    .filter((group) => group.clients.length > 0);
});

// Section-level expand/collapse. Writing straight into `expanded` means the
// section carets need no state of their own and persist with everything else.
function toggleAll(groups, open) {
  groups.forEach((group) => {
    expanded[group.key] = open;
  });
}

const allExpanded = (groups) => groups.length > 0 && groups.every((group) => expanded[group.key]);

const allManagersExpanded = computed(() => allExpanded(managerGroups.value));
const allStaffExpanded = computed(() => allExpanded(staffGroups.value));

// Filters and expanded rows survive leaving the page. sessionStorage over Vuex:
// it also survives a refresh, and is per-tab.
const STATE_KEY = "client-assignments-view-state";

function saveState() {
  try {
    sessionStorage.setItem(
      STATE_KEY,
      JSON.stringify({
        manager: filterManager.value,
        staff: filterStaff.value,
        client: filterClient.value,
        status: filterStatus.value,
        expanded: Object.keys(expanded).filter((key) => expanded[key]),
      })
    );
  } catch (error) {
    // Private browsing or full quota; persistence is only a convenience.
  }
}

// Runs after load() so vanished ids can be dropped — a saved filter naming a
// departed staff member would render an empty page for no visible reason.
// Expanded keys need no check: one naming a missing group never matches.
function restoreState() {
  let saved = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(STATE_KEY));
  } catch (error) {
    return;
  }
  if (!saved) return;

  const knows = (people, id) => people.value.some((user) => user.id === id);

  if (saved.manager === "unassigned" || knows(accountManagers, saved.manager)) {
    filterManager.value = saved.manager;
  }
  if (Array.isArray(saved.staff)) {
    filterStaff.value = saved.staff.filter((id) => knows(accountStaff, id));
  }
  if (clients.value.some((client) => client.id === saved.client)) {
    filterClient.value = saved.client;
  }
  if (Array.isArray(saved.status)) {
    filterStatus.value = saved.status.filter((value) =>
      STATUS_OPTIONS.some((option) => option.value === value)
    );
  }
  if (Array.isArray(saved.expanded)) {
    saved.expanded.forEach((key) => {
      expanded[key] = true;
    });
  }
}

watch([filterManager, filterStaff, filterClient, filterStatus, expanded], saveState, {
  deep: true,
});

async function load() {
  loading.value = true;
  const token = store.state.auth.token;
  try {
    clients.value = (await getClientAssignments(token)) || [];
    restoreState();
  } catch (error) {
    $toast.open({ message: "Failed to load client assignments", type: "error", duration: 3000 });
  }
  loading.value = false;
}

onMounted(() => {
  document.addEventListener("click", closeMenuOnOutsideClick);
  load();
});

onUnmounted(() => document.removeEventListener("click", closeMenuOnOutsideClick));
</script>
